import { QueryParameterBag } from '@smithy/types'

import {
  DroidMapMetadata,
  ServiceClient,
  ServiceClientContext,
} from '@droidmap/shared-service-contract'
import { DroidMapError, HttpStatus } from '@droidmap/shared-utils'

import {
  FEEDBACK_MIN_SUBMIT_INTERVAL_SECONDS,
  FeedbackRateLimitedError,
  SubmitFeedbackInput,
  SubmitFeedbackResult,
} from '../models/feedback.js'
import {
  GetUserByEmailInput,
  GetUserInput,
  UpdateUserInput,
  User,
} from '../models/user.js'

export class UserServiceClient extends ServiceClient {
  public static readonly SERVICE_NAME = 'UserService'

  constructor(input: ServiceClientContext) {
    super({
      ...input,
      serviceName: UserServiceClient.SERVICE_NAME,
    })
  }

  getUser = async (
    getUserInput: GetUserInput,
    metadata?: DroidMapMetadata,
  ): Promise<User> => {
    const user = await this.callApiRouteWithToken<undefined, User>({
      method: 'GET',
      path: `/users/${getUserInput.id}`,
      metadata,
    })

    return user
  }

  getUserByEmail = async (
    getUserByEmailInput: GetUserByEmailInput,
    metadata?: DroidMapMetadata,
  ): Promise<User> => {
    const queryParameters: QueryParameterBag = {
      email: getUserByEmailInput.userEmail,
    }

    const user = await this.callApiRouteWithToken<undefined, User>({
      method: 'GET',
      path: `/users/by-email`,
      queryParameters,
      metadata,
    })

    return user
  }

  updateUser = async (
    updateUserInput: UpdateUserInput,
    metadata?: DroidMapMetadata,
  ): Promise<User> => {
    const user = await this.callApiRouteWithToken<Partial<User>, User>({
      method: 'PATCH',
      path: `/users/${updateUserInput.id}`,
      body: updateUserInput,
      metadata,
    })

    return user
  }

  /**
   * Submit an in-app support / feedback report.
   *
   * Deliberately does **not** go through `callApiRouteWithToken`: that path
   * runs `retryFetch`, which treats 429 as retryable. Here a 429 is the
   * server's rate limiter answering, so retrying would burn five more
   * attempts against the hourly budget and surface as a generic failure
   * several seconds later. One attempt; 429 becomes a
   * {@link FeedbackRateLimitedError} the widget can render as a countdown.
   *
   * Identity is resolved server-side from the session — nothing in `input`
   * names a user, and anything that did would be ignored.
   */
  submitFeedback = async (
    input: SubmitFeedbackInput,
    metadata: DroidMapMetadata,
  ): Promise<SubmitFeedbackResult> => {
    const token = await this.getToken()

    const response = await fetch(
      new Request(`${this.endpoint}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.buildRequestHeaders(token, metadata),
        },
        body: JSON.stringify(input),
      }),
    )

    if (response.status === HttpStatus.TOO_MANY_REQUESTS_429) {
      const retryAfterSeconds = parseRetryAfterSeconds(
        response.headers.get('retry-after'),
      )
      // Consume the body so the connection can be reused.
      await response.text()
      throw new FeedbackRateLimitedError(retryAfterSeconds)
    }

    if (!response.ok) {
      await response.text()
      throw new DroidMapError(
        `Failed to submit feedback - ${response.status} ${response.statusText}`,
      )
    }

    return (await response.json()) as SubmitFeedbackResult
  }
}

/**
 * Read the `Retry-After` header as whole seconds, falling back to the
 * configured minimum gap when the header is missing or unparseable (a proxy
 * may strip it). Never returns 0, so the UI always shows a real countdown.
 */
const parseRetryAfterSeconds = (header: string | null): number => {
  const parsed = Number(header)
  return Number.isFinite(parsed) && parsed > 0
    ? Math.ceil(parsed)
    : FEEDBACK_MIN_SUBMIT_INTERVAL_SECONDS
}
